import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Icon,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core"

import { useSnackbarContext } from "context/notification"
import { getIssuedDocumentsService, uploadRetentionsService } from "lib"
import { RENTENTION_TYPES } from "mocks/retentionInfo.mock"
import React from "react"
import { IRetentionKey, RetentionUploadRequest } from "types/request"
import { IIssuedDocument } from "types/response/issuedDocumentsResponse"
import { IRetentionInfo } from "types/retentionInfoType"
import iconFactura from "../../assets/img/icons/01.png"
import iconNotaCredito from "../../assets/img/icons/04.png"
import RetentionInfo from "./RetentionInfo"
import logo from "assets/img/logo-bg-transparent.svg"
import { RetentionValidationResult } from "utils"

const UploadRetentions = () => {
  const [rif, setRif] = React.useState<string>("")
  const [documentsLoading, setDocumentsLoading] = React.useState<boolean>(false)
  const [uploadingRets, setUploadingRets] = React.useState<boolean>(false)
  const [documents, setDocuments] = React.useState<IIssuedDocument[]>([])
  const [hasSearched, setHasSearched] = React.useState<boolean>(false)
  const [openDocumentId, setOpenDocumentId] = React.useState<number | null>(
    null,
  )
  const [retentionsByDocument, setRetentionsByDocument] = React.useState<
    Record<number, RetentionUploadRequest[]>
  >({})

  const [retentionValidations, setRetentionValidations] = React.useState<
    Record<string, RetentionValidationResult>
  >({})

  const handleValidationChange = (
    retKey: string,
    result: RetentionValidationResult,
  ) => {
    setRetentionValidations((prev) => ({ ...prev, [retKey]: result }))
  }
  const { showMessage } = useSnackbarContext()

  const handleClear = () => {
    setRif("")
    setHasSearched(false)
    setDocuments([])
  }

  const getDocumentTypeName = (documentType: number): string => {
    switch (documentType) {
      case 1:
        return "FACTURA"
      case 5:
        return "NOTA DE CREDITO"
      default:
        return "DESCONOCIDO"
    }
  }

  const tipoDocumentImagen = (documentType: number) => {
    switch (documentType) {
      case 1:
        return iconFactura
      case 5:
        return iconNotaCredito
      default:
        return ""
    }
  }

  const isDocumentReady = (documentId: number): boolean => {
    const retentions = retentionsByDocument[documentId]
    if (!retentions || retentions.length === 0) return false
    return retentions.every(
      (r) => retentionValidations[r.retentionKey]?.isValid === true,
    )
  }

  const getRetentionLabel = (retKey: IRetentionKey): string => {
    const labels: Record<IRetentionKey, string> = {
      retIva75: "IVA 75%",
      retISLR2: "ISLR 2%",
      retISLR3: "ISLR 3%",
    }
    return labels[retKey]
  }

  const getDocumentReadyMessage = (documentId: number): string => {
    const retentions = retentionsByDocument[documentId]
    if (!retentions || retentions.length === 0) return ""

    for (const r of retentions) {
      const result = retentionValidations[r.retentionKey]
      if (!result?.isValid) {
        return (
          result?.message ?? `${getRetentionLabel(r.retentionKey)}: Pendiente`
        )
      }
    }
    return ""
  }

  const handleGetDocuments = async () => {
    try {
      setDocumentsLoading(true)
      setHasSearched(true)
      const documentResponse = await getIssuedDocumentsService(rif)
      setDocuments(documentResponse)
    } catch (error) {
      console.log("Error", error)
    } finally {
      setDocumentsLoading(false)
    }
  }

  const getRetencionesAplicables = (doc: IIssuedDocument): IRetentionInfo[] => {
    const { bases } = doc.dataInvoice
    return RENTENTION_TYPES.filter((retention) => bases[retention.baseKey] > 0)
  }

  const buildInitialRetentions = (
    doc: IIssuedDocument,
  ): RetentionUploadRequest[] => {
    return getRetencionesAplicables(doc).map((ret) => ({
      documentId: doc.documentId,
      retentionKey: ret.retKey as IRetentionKey,
      pdf: null,
      serialNumber: "",
      infoCustomer: doc.dataInvoice.header.customer,
      retainedValue: "",
    }))
  }

  const handleToggleDocument = (doc: IIssuedDocument) => {
    setRetentionsByDocument((prev) => {
      if (prev[doc.documentId]) return prev
      return {
        ...prev,
        [doc.documentId]: buildInitialRetentions(doc),
      }
    })
    setOpenDocumentId((prev) =>
      prev === doc.documentId ? null : doc.documentId,
    )
  }

  const handleSubmitDocument = async (documentId: number) => {
    const retentions = retentionsByDocument[documentId]

    if (!retentions || retentions.length === 0) {
      showMessage("No hay retenciones para enviar.", "warning")
      return
    }

    // Validación básica
    const invalid = retentions.some((r) => {
      const numericValue = Number(r.retainedValue)
      return (
        !r.pdf ||
        !r.serialNumber ||
        r.retainedValue === "" ||
        isNaN(numericValue) ||
        numericValue <= 0
      )
    })

    if (invalid) {
      showMessage(
        "Completa los datos ( valor retenido, serial, pdf ) de las retenciones",
        "error",
      )
      return
    }

    const payload: RetentionUploadRequest[] = retentions.map((r) => ({
      documentId: r.documentId,
      pdf: r.pdf,
      serialNumber: r.serialNumber,
      infoCustomer: r.infoCustomer,
      retainedValue: r.retainedValue,
      retentionKey: r.retentionKey,
    }))
    try {
      setUploadingRets(true)
      const response = await uploadRetentionsService(payload)
      if (response.success) {
        // llamar ep a actualizar estado de documentos
        await handleGetDocuments()
        showMessage(response.message, "success")
      } else {
        showMessage(response.message, "error")
      }
    } catch (error) {
      console.log("Error", error)
    } finally {
      setUploadingRets(false)
    }
  }

  return (
    <Box
      p={2}
      style={{
        width: "98%",
        height: "100vh",
        overflowY: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        justifyContent="center"
        mb={3}
        style={{ flexShrink: 0 }}
      >
        <Typography
          variant="h5"
          style={{ fontWeight: 700, color: "#37474f", letterSpacing: 1 }}
        >
          RETENCIONES DE FACTURAS
        </Typography>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={2}
        style={{ position: "relative", flexShrink: 0 }}
      >
        {/* INPUT + BOTÓN centrados */}
        <Box display="flex" alignItems="center">
          <TextField
            size="small"
            placeholder={"Ingresa tu RIF para consultar tus documentos"}
            classes={{ root: "search-text-field" }}
            label="RIF del cliente"
            value={rif}
            onChange={(e) => {
              const value = e.target.value.trim()
              setRif(value)
              if (value.length === 0) setHasSearched(false)
            }}
            variant="outlined"
            style={{ width: "360px" }}
            InputProps={{
              style: { height: 40, padding: 0 },
              endAdornment: (
                <InputAdornment position="end" style={{ marginRight: "5px" }}>
                  <Tooltip title="Borrar">
                    <IconButton
                      onClick={handleClear}
                      size="small"
                      style={{ display: rif ? "flex" : "none" }}
                    >
                      <Icon style={{ fontSize: 25 }}>close</Icon>
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleGetDocuments()
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleGetDocuments}
            disabled={documentsLoading || rif.trim() === ""}
            style={{ marginLeft: "12px", position: "relative" }}
            endIcon={
              documentsLoading ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                <Icon>search</Icon>
              )
            }
          >
            {documentsLoading ? "Consultando........." : "Consultar documentos"}
          </Button>
        </Box>

        {/* LOGO anclado a la derecha sin afectar el centrado */}
        <Box
          style={{
            position: "absolute",
            right: 0,
            display: "flex",
            alignItems: "center",
            background: "#FFCC00",
            padding: "12px",
          }}
        >
          <img src={logo} alt="Logo" style={{ height: 30, width: "auto" }} />
        </Box>
      </Box>

      {documents.length > 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          style={{ flex: 1, overflow: "hidden" }}
        >
          {documents[0]?.dataInvoice?.header?.customer?.customerName && (
            <Box
              display="flex"
              justifyContent="center"
              mb={2}
              style={{ flexShrink: 0 }}
            >
              <Typography variant="h6" style={{ color: "#78909c" }}>
                {documents[0].dataInvoice.header.customer.customerName}
              </Typography>
            </Box>
          )}
          <Box
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: 4,
            }}
          >
            {documents.map((doc) => (
              <Paper
                key={doc.documentId}
                elevation={0}
                style={{
                  padding: "10px",
                  marginBottom: "16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <img
                      alt="logoDocumento"
                      src={tipoDocumentImagen(doc.documentType)}
                      style={{
                        width: "35px",
                        height: "auto",
                        borderRadius: "3px",
                        marginRight: "12px",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600 }}
                      >
                        {getDocumentTypeName(doc.documentType)}
                      </Typography>
                      <Box
                        style={{
                          display: "flex",
                          gap: 16,
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{ color: "#78909c" }}
                        >
                          {doc.documentDate}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ color: "#b0bec5" }}
                        >
                          |
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ color: "#78909c" }}
                        >
                          Serial: {doc.serialNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Button
                    endIcon={
                      <Icon>
                        {openDocumentId === doc.documentId
                          ? "arrow_drop_up"
                          : "arrow_drop_down"}
                      </Icon>
                    }
                    variant="contained"
                    color="primary"
                    onClick={() => handleToggleDocument(doc)}
                  >
                    {openDocumentId === doc.documentId
                      ? `Cerrar Detalles ${getDocumentTypeName(doc.documentType)}`
                      : `Ver Detalles ${getDocumentTypeName(doc.documentType)}`}
                  </Button>
                </Box>

                <Collapse in={openDocumentId === doc.documentId}>
                  <Box style={{ marginTop: 2, padding: 2, borderRadius: 4 }}>
                    {retentionsByDocument[doc.documentId]?.map(
                      (retentionUpload) => {
                        const retention = RENTENTION_TYPES.find(
                          (r) => r.retKey === retentionUpload.retentionKey,
                        )!
                        return (
                          <Box
                            key={retentionUpload.retentionKey}
                            sx={{ marginBottom: 16 }}
                          >
                            <RetentionInfo
                              retention={retention}
                              document={doc}
                              value={retentionUpload}
                              onChange={(updated) => {
                                setRetentionsByDocument((prev) => ({
                                  ...prev,
                                  [doc.documentId]: prev[doc.documentId].map(
                                    (r) =>
                                      r.retentionKey === updated.retentionKey
                                        ? updated
                                        : r,
                                  ),
                                }))
                              }}
                              onValidationChange={handleValidationChange}
                            />
                          </Box>
                        )
                      },
                    )}
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: 2,
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    {!isDocumentReady(doc.documentId) &&
                      openDocumentId === doc.documentId && (
                        <Typography
                          variant="caption"
                          style={{ color: "#d32f2f" }}
                        >
                          ⚠ {getDocumentReadyMessage(doc.documentId)}
                        </Typography>
                      )}
                    <Button
                      style={{
                        backgroundColor: isDocumentReady(doc.documentId)
                          ? "#FFB221"
                          : "#b0bec5",
                      }}
                      variant="contained"
                      disabled={!isDocumentReady(doc.documentId)}
                      onClick={() => handleSubmitDocument(doc.documentId)}
                      endIcon={
                        uploadingRets ? (
                          <CircularProgress
                            size={20}
                            style={{ color: "white" }}
                          />
                        ) : (
                          <Icon>send</Icon>
                        )
                      }
                    >
                      {uploadingRets
                        ? "Enviando........."
                        : "Enviar Retenciones"}
                    </Button>
                  </Box>
                </Collapse>
              </Paper>
            ))}
          </Box>
        </Box>
      ) : hasSearched ? (
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Icon style={{ fontSize: 48, color: "#b0bec5" }}>inbox</Icon>
          <Typography
            variant="body1"
            style={{ color: "#78909c", fontWeight: 600 }}
          >
            No hay documentos para mostrar
          </Typography>
          <Typography variant="body2" style={{ color: "#b0bec5" }}>
            No se encontraron documentos asociados a este RIF
          </Typography>
        </Box>
      ) : (
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Icon style={{ fontSize: 48, color: "#b0bec5" }}>manage_search</Icon>
          <Typography
            variant="body1"
            style={{ color: "#78909c", fontWeight: 600 }}
          >
            Ingresa un RIF para comenzar
          </Typography>
          <Typography variant="body2" style={{ color: "#b0bec5" }}>
            Los documentos asociados aparecerán aquí
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default UploadRetentions
